import HashListener from "/WebInterface/new-ui/modules/hash-listener.js";
import { growl } from "/WebInterface/new-ui/modules/toast.js";
import { Confirm } from "/WebInterface/new-ui/modules/alert.js";
import { initSharing } from "/WebInterface/new-ui/modules/modals/sharing/sharing.js";
import Modal from "/WebInterface/new-ui/modules/modal.js";
import downloadingQueue from "/WebInterface/new-ui/modules/advanced-download/downloading-queue.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import store from "/WebInterface/new-ui/store/index.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import userActions from "/WebInterface/new-ui/assets/js/app/user-actions.js";
import {
  showCustomUploadFormModal,
  updateCustomUploadFormModal,
  updateCustomForm,
  isCustomUploadFormAvailable,
  showCommonUploadForm,
} from "/WebInterface/new-ui/upload/modules/upload-form/upload-form.js";
import { getCustomForm } from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import { validate } from "/WebInterface/new-ui/assets/js/utils/validate.js";
import { fileTypes } from "/WebInterface/new-ui/assets/js/constants.js";

// Initialize Hash Listener
const hashListener = new HashListener({});

let crushdropUploadPathOverride = null;
let redirectAfterUploadTimer = null;
let redirectAfterUploadActive = false;

const clearRedirectAfterUploadTimer = () => {
  if (redirectAfterUploadTimer) {
    clearInterval(redirectAfterUploadTimer);
    redirectAfterUploadTimer = null;
  }
};

const beginRedirectCountdown = (location, seconds, message) => {
  const delay = Number.parseInt(seconds, 10);
  if (!location || !Number.isFinite(delay) || delay <= 0) return null;

  let remaining = delay;
  const updateMessage = () => {
    message.textContent = t(
      "redirectAfterUploadCountdown",
      { seconds: remaining },
      "You will be redirected in {seconds} seconds."
    );
  };
  const redirectNow = () => {
    clearRedirectAfterUploadTimer();
    window.redirectPage(location);
  };

  updateMessage();
  redirectAfterUploadTimer = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      redirectNow();
      return;
    }
    updateMessage();
  }, 1000);

  return redirectNow;
};

const uploadSummaryBody = (files, countdownMessage) => {
  const body = utils.createElement("div", ["upload-summary"], {});
  if (countdownMessage) {
    countdownMessage.classList.add("upload-summary-countdown");
    body.appendChild(countdownMessage);
  }

  const items = (Array.isArray(files) ? files : []).filter((file) => {
    const name = String(file?.name || "").trim();
    const path = String(file?.path || "").trim();
    return name || path;
  });

  if (items.length === 0) {
    const empty = utils.createElement("p", ["upload-summary-empty"], {});
    empty.textContent = t(
      "uploadSummaryEmpty",
      {},
      "No files were uploaded."
    );
    body.appendChild(empty);
    return body;
  }

  const count = utils.createElement("p", ["upload-summary-count"], {});
  count.textContent =
    items.length === 1
      ? t("uploadSummaryOne", {}, "1 file uploaded")
      : t(
          "uploadSummaryMany",
          { count: items.length },
          "{count} files uploaded"
        );
  body.appendChild(count);

  const list = utils.createElement("ul", ["upload-summary-list"], {});
  items.forEach((file) => {
    const nameText = String(file.name || "").trim();
    const pathText = String(file.path || "").trim();
    const item = utils.createElement("li", ["upload-summary-item"], {});
    const iconWrap = utils.createElement("span", ["upload-summary-icon"], {});
    const icon = utils.createElement(
      "i",
      [
        pathText.endsWith("/") ? "icon-folder-close" : "icon-file",
        "text-accentMain",
        "text-lg",
      ],
      {}
    );
    iconWrap.appendChild(icon);

    const copy = utils.createElement("div", ["upload-summary-copy"], {});
    if (nameText) {
      const name = utils.createElement(
        "p",
        ["upload-summary-name", "has-text-weight-semibold"],
        {}
      );
      name.textContent = nameText;
      copy.appendChild(name);
    }
    if (pathText) {
      const path = utils.createElement(
        "p",
        ["upload-summary-path", "text-sm", "word-break-all"],
        {}
      );
      path.textContent = pathText;
      copy.appendChild(path);
    }

    item.appendChild(iconWrap);
    item.appendChild(copy);
    list.appendChild(item);
  });
  body.appendChild(list);
  return body;
};
let crushdropHostListenersRegistered = false;
let crushdropSetCurrentDirInfoSubscribed = false;
let lastCrushdropNoWriteGrowlKey = "";

function normalizeCrushdropVfsPath(p) {
  if (p == null || p === "") return null;
  let s = String(p).trim().replace(/\/+/g, "/");
  if (!s.startsWith("/")) s = "/" + s;
  return s;
}

function vfsPathSameDirectory(a, b) {
  if (a == null || b == null) return false;
  const pa = String(a).trim().replace(/\/+/g, "/");
  const pb = String(b).trim().replace(/\/+/g, "/");
  const ra = pa.replace(/\/+$/, "") || "/";
  const rb = pb.replace(/\/+$/, "") || "/";
  return ra === rb;
}

function directoryListingHashValue(next) {
  const n = normalizeCrushdropVfsPath(next);
  if (!n) return "/";
  return n.endsWith("/") ? n : n + "/";
}

function syncLocationHashToCrushdropListingPath(next) {
  const target = directoryListingHashValue(next);
  let currentPathFromHash = hashListener.getPath();
  try {
    currentPathFromHash = decodeURIComponent(currentPathFromHash);
  } catch (ex) {
    currentPathFromHash = unescape(currentPathFromHash);
  }
  if (vfsPathSameDirectory(currentPathFromHash, target)) {
    return false;
  }
  document.location.hash = target;
  return true;
}

function readCrushdropPathFromHostDocument() {
  let attr = "";
  try {
    attr =
      document.documentElement.getAttribute("data-crushdrop-upload-path") || "";
  } catch (e) {
    attr = "";
  }
  if (attr.trim() !== "") {
    return normalizeCrushdropVfsPath(attr);
  }
  if (
    typeof window.__crushdropUploadPath === "string" &&
    window.__crushdropUploadPath.trim() !== ""
  ) {
    return normalizeCrushdropVfsPath(window.__crushdropUploadPath);
  }
  return null;
}

function decodeHashPathForCrushdrop() {
  let hp = hashListener.getPath();
  try {
    hp = decodeURIComponent(hp);
  } catch (ex) {
    hp = unescape(hp);
  }
  return hp;
}

function listingPathsAlignedForCrushdrop() {
  if (!crushdropUploadPathOverride) return false;
  const hp = decodeHashPathForCrushdrop();
  const listed = store?.currentDirectoryInfo?.path;
  const ov = crushdropUploadPathOverride;
  if (vfsPathSameDirectory(hp, ov)) return true;
  if (listed && vfsPathSameDirectory(listed, ov)) return true;
  if (listed && typeof listed === "string") {
    const segmentKey = (s) =>
      String(normalizeCrushdropVfsPath(s) || "")
        .toLowerCase()
        .replace(/^\/+/, "")
        .replace(/\/+$/, "");
    const la = segmentKey(listed);
    const ob = segmentKey(ov);
    if (ob && (la === ob || la.endsWith("/" + ob))) return true;
  }
  return false;
}

function maybeCrushdropNoWriteUploadGrowl() {
  if (crushdropUploadPathOverride == null) return;
  const info = store?.currentDirectoryInfo;
  if (!info || typeof info.privs !== "string") return;
  if (!listingPathsAlignedForCrushdrop()) return;
  const privs = info.privs;
  if (privs.includes("(write)")) {
    lastCrushdropNoWriteGrowlKey = "";
    return;
  }
  const key = crushdropUploadPathOverride + "|" + privs;
  if (key === lastCrushdropNoWriteGrowlKey) return;
  lastCrushdropNoWriteGrowlKey = key;
  growl(t("youDoNotHavePermissionToUpload"), t("uploadNotAllowed"), 5000, true);
}

function notifyUploadIframeCrushdropPath(pathStr) {
  const iframe = document.querySelector("#fileUploadIframe");
  iframe?.contentWindow?.postMessage(
    { key: "crushdropUploadPath", value: pathStr },
    "*"
  );
}

function applyCrushdropUploadPathOnHost(raw) {
  if (typeof raw !== "string") return;
  const next = normalizeCrushdropVfsPath(raw.trim());
  if (next == null) return;

  if (
    crushdropUploadPathOverride != null &&
    vfsPathSameDirectory(next, crushdropUploadPathOverride)
  ) {
    return;
  }

  crushdropUploadPathOverride = next;
  document.documentElement.setAttribute("data-crushdrop-upload-path", next);
  window.__crushdropUploadPath = next;
  notifyUploadIframeCrushdropPath(next);
  const navigated = syncLocationHashToCrushdropListingPath(next);
  if (!navigated) {
    try {
      store.dispatch("refreshListing");
    } catch (e) {}
  }
  queueMicrotask(() => {
    maybeCrushdropNoWriteUploadGrowl();
  });
}

function registerCrushdropHostListenersOnce() {
  if (crushdropHostListenersRegistered) return;
  crushdropHostListenersRegistered = true;
  window.addEventListener("crushdrop-upload-path", (e) => {
    const p = e.detail && e.detail.path;
    if (typeof p === "string") {
      applyCrushdropUploadPathOnHost(p);
    }
  });
  window.addEventListener("message", (e) => {
    if (e.origin !== window.location.origin) return;
    if (
      !e.data ||
      e.data.key !== "crushdropUploadPath" ||
      typeof e.data.value !== "string"
    ) {
      return;
    }
    const iframe = document.querySelector("#fileUploadIframe");
    if (iframe && e.source === iframe.contentWindow) {
      applyCrushdropUploadPathOnHost(e.data.value);
    }
  });

  window.addEventListener("hashchange", () => {
    if (crushdropUploadPathOverride == null) return;
    let hp = hashListener.getPath();
    try {
      hp = decodeURIComponent(hp);
    } catch (ex) {
      hp = unescape(hp);
    }
    if (vfsPathSameDirectory(hp, crushdropUploadPathOverride)) {
      return;
    }
    crushdropUploadPathOverride = null;
    lastCrushdropNoWriteGrowlKey = "";
    document.documentElement.removeAttribute("data-crushdrop-upload-path");
    try {
      delete window.__crushdropUploadPath;
    } catch (err) {
      window.__crushdropUploadPath = undefined;
    }
  });

  if (!crushdropSetCurrentDirInfoSubscribed) {
    crushdropSetCurrentDirInfoSubscribed = true;
    store.subscribe("setCurrentDirInfo", () => {
      queueMicrotask(() => {
        maybeCrushdropNoWriteUploadGrowl();
      });
    });
  }
}

// Declare uploadModal for later use
let uploadModal;

// Function to show the upload modal
export const onShowUploadModal = () => {
  if (uploadModal && !uploadModal.visible()) {
    uploadModal.show(true);
  }
};

export const showUploadModalAndListFiles = (files) => {
  if (uploadModal && !uploadModal.visible()) {
    uploadModal.show(true);
  }

  const iframe = _$("#fileUploadIframe").el();
  iframe?.contentWindow?.postMessage({
    key: "addFilesToQueue",
    value: files,
  });
};

// Middleware function for loading new upload iframe
export const loadNewUploadIFrameMiddleWare = async () => {
  registerCrushdropHostListenersOnce();
  const { privs } = store?.currentDirectoryInfo;

  let bodyNoWriteDropHandlersInstalled = false;
  let bodyNoWriteDragFromApp = false;

  const installBodyNoWriteDropHandlersOnce = () => {
    if (bodyNoWriteDropHandlersInstalled) {
      return;
    }
    bodyNoWriteDropHandlersInstalled = true;

    _$("body").on("dragstart", () => {
      bodyNoWriteDragFromApp = true;
    });

    _$("body").on("drop", (e) => {
      e.preventDefault();
      if (
        !e.dataTransfer ||
        !e.dataTransfer.files ||
        e.dataTransfer.files.length === 0
      ) {
        bodyNoWriteDragFromApp = false;
        return;
      }

      const { privs: bodyDropPrivs } = store?.currentDirectoryInfo || {};
      if (bodyDropPrivs?.includes("(write)")) {
        bodyNoWriteDragFromApp = false;
        return;
      }

      if (!bodyNoWriteDragFromApp) {
        growl(
          t("youDoNotHavePermissionToUpload"),
          t("uploadNotAllowed"),
          5000,
          true
        );
      }
      bodyNoWriteDragFromApp = false;
    });

    window.addEventListener("dragend", () => {
      bodyNoWriteDragFromApp = false;
    });
  };

  if (!privs?.includes("(write)")) {
    installBodyNoWriteDropHandlersOnce();
  }

  const isElectronUploadOnlyApp = utils.isElectron();

  // const initialUploadModalWidth =
  //   store.userInfo.userCustomizations?.["upload_UploadPanelDefaultWidth"]
  //     ?.value;

  // Create upload modal
  uploadModal = new Modal({
    title: "filesToUpload",
    body: `<div class="w-full h-full">
    <iframe
      class="w-full h-full"
      frameborder="0"
      id="fileUploadIframe"
      src="/WebInterface/new-ui/upload/index.html?embed=true"></iframe>
  </div>`,
    animate: false,
    show: isElectronUploadOnlyApp, // If Electron App
    backdrop: false,
    movable: !isElectronUploadOnlyApp,
    closable: isElectronUploadOnlyApp,
    hasFooter: false,
    showHeader: !isElectronUploadOnlyApp,
    destroyOnClose: false,
    uniqueId: "UI_Upload_NEW",
    extraClasses: ["files-to-upload-modal"],
    minWidth: "921px",
    minHeight: "700px",
    onAfterResize: () => {
      const iframe = _$("#fileUploadIframe").el();
      iframe.contentWindow.postMessage({
        key: "uploadModalResize",
      });
    },
    onAfterShow: () => {
      const overrideFontFamily = utils.getUserCustomizationValue(
        "fontFace",
        true
      );

      const iframe = _$("#fileUploadIframe").el();
      iframe.contentWindow.postMessage({
        key: "overrideFontFamily",
        value:
          overrideFontFamily ||
          'BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
      });
    },
    // initialWidth: `${initialUploadModalWidth}px`,
  });

  if (isElectronUploadOnlyApp) {
    _$("body").attr("data-electron-upload-host", "true");
  }

  // if (isElectronUploadOnlyApp) {
  // _$(uploadModal?.modal?.body).addClass("py-2");
  // _$(uploadModal?.modal?.body).addClass("px-1");
  // }

  const { customUploadForm, customCommonUploadForm, currentDirectoryInfo } =
    store;

  if (!customUploadForm) {
    try {
      const customUploadForm = await getCustomForm("uploadForm");
      store.dispatch("setCustomUploadForm", customUploadForm);
    } catch (error) {
      // console.log(error);
    }
  }

  if (!customCommonUploadForm?.formDetails) {
    try {
      const customCommonUploadForm = await getCustomForm("commonUploadForm");
      store.dispatch("setCustomCommonUploadForm", customCommonUploadForm);
    } catch (error) {
      // console.log(error);
    }
  }

  // Drag and drop related events
  let isDraggingCrushFTPItem = false;

  const disableDragAndDropUpload = utils.getUserCustomizationValue(
    "disableDragDropUpload"
  );

  if (
    !disableDragAndDropUpload &&
    !(
      currentDirectoryInfo &&
      currentDirectoryInfo.privs &&
      currentDirectoryInfo.privs.includes("(locked)") &&
      currentDirectoryInfo.privs.includes("(expired)")
    )
  ) {
    window.addEventListener("dragover", (e) => {
      e.preventDefault();
      const { privs } = store?.currentDirectoryInfo;
      if (!privs?.includes("(write)")) {
        installBodyNoWriteDropHandlersOnce();
        return;
      }

      if (!isDraggingCrushFTPItem && !uploadModal.visible()) {
        userActions.invoke({
          name: "performAction('upload')",
          draggingEvent: true,
        });
      }
    });

    window.addEventListener("dragstart", () => {
      isDraggingCrushFTPItem = true;
    });

    window.addEventListener("dragend", (e) => {
      e.preventDefault();
      isDraggingCrushFTPItem = false;
    });
  }

  window.isElectron = utils.isElectron;
  window.getIPCRenderer = utils.getIPCRenderer;

  window.getCurrentTheme = () => {
    const theme = _$("html").attr("data-theme");
    return theme;
  };

  window.getCurrentLanguage = () => {
    const lang = _$("html").attr("lang");
    return lang;
  };

  // Define functions accessible globally
  window.getDraggingItemIsFromCrushFTP = () => {
    return isDraggingCrushFTPItem;
  };

  window.currentPath = () => {
    if (
      crushdropUploadPathOverride != null &&
      crushdropUploadPathOverride !== ""
    ) {
      return crushdropUploadPathOverride;
    }
    let path = hashListener.getPath();
    try {
      path = decodeURIComponent(path);
    } catch (ex) {
      path = unescape(path);
    }
    return path;
  };

  window.setCrushdropUploadPath = (p) => {
    if (typeof p === "string") applyCrushdropUploadPathOnHost(p);
  };

  window.uploadNotifications = (uploadConfig) => {
    if (window.parent.postMessage) {
      window.parent.postMessage(uploadConfig, "*");
    }
  };

  window.getBaseURL = () => {
    let baseURL = document.location.href + "";
    if (baseURL.indexOf("#") >= 0)
      baseURL = baseURL.substring(0, baseURL.indexOf("#"));

    const customShareLinkURL = utils.getUserCustomizationValue(
      "customShareLinkURL",
      true
    );

    if (customShareLinkURL) {
      baseURL = customShareLinkURL;
    }

    return baseURL;
  };

  window.showCustomUploadFormModal = showCustomUploadFormModal;
  window.updateCustomUploadFormModal = updateCustomUploadFormModal;
  window.updateCustomForm = updateCustomForm;
  window.isCustomUploadFormAvailable = isCustomUploadFormAvailable;
  window.showCommonUploadForm = showCommonUploadForm;
  window.validate = validate;

  window.growl = growl;

  window.currentHash = () => {
    return hashListener.getHash();
  };

  window.commonUploadForm = () => {
    const customCommonUploadForm = store?.customCommonUploadForm;
    return customCommonUploadForm;
  };

  window.currentDirPrivs = () => {
    const { privs } = store?.currentDirectoryInfo;
    return privs;
  };

  window.getCurrentDirInfo = () => {
    return store?.currentDirectoryInfo;
  };

  window.getCurrentTreeItems = () => {
    return utils.deepCopy(store.files);
  };

  window.refreshListing = () => {
    store.dispatch("refreshListing");
  };

  window.logout = () => {
    store.dispatch("logoutRequested");
  };

  window.getUserInfo = () => {
    return store.userInfo;
  };

  window.getMetaUserInfo = () => {
    const userInfo = store.userInfo;

    const metaUserInfo =
      Object.fromEntries(
        Object.entries(userInfo).filter(([key]) => key.startsWith("META_"))
      ) || {};
    return metaUserInfo;
  };

  window.getAlternateDomains = () => {
    const altHttpDomains = store.userInfo.alt_http_domains;
    const serverNames = [];

    if (typeof altHttpDomains !== "string") return serverNames;

    let altDomains = altHttpDomains
      ? altHttpDomains.replace("alt_http_domains", "")
      : "";

    if (!altDomains) return serverNames;

    let altArr = [];

    if (altDomains.indexOf(";") >= 0) altArr = altDomains.split(";");
    else altArr = altDomains.split("\n");

    const port = window.location.port;
    const protocol = window.location.protocol;

    altArr.forEach((item) => {
      let serverName = item;

      if (!serverName) return;
      serverName = serverName.replace("\n", "");

      if (
        !serverName.startsWith("http://") &&
        !serverName.startsWith("https://")
      )
        serverName = protocol + "//" + serverName;
      if (serverName.endsWith("/")) serverName = serverName.slice(0, -1);
      if (serverName.split(":").length < 3) {
        serverName += ":" + port;
      }

      serverNames.push(serverName);
    });

    return serverNames;
  };

  window.getUserCustomizationValue = (customizationType, value) => {
    return utils.getUserCustomizationValue(customizationType, value);
  };

  window.closeUploadModal = uploadModal.hide;

  window.initSharing = initSharing;

  window.initQuickSharing = (files) => {
    const fileListContext = files.paths
      .split("\r\n")
      .map((file) => ({ href_path: file, type: fileTypes.FILE }));
    userActions.invoke({
      name: "quickShare",
      context: fileListContext,
    });
  };

  window.confirmAlert = Confirm;

  window.setUploadingStatus = (type, status) => {
    store.dispatch("setChunkProcessingStatus", {
      type,
      status,
    });
  };

  window.checkForAutoUpload = () => {
    const isElectronUploadOnlyApp = utils.isElectron();
    return (
      isElectronUploadOnlyApp || store?.userOptions?.preference?.autoStartUpload
    );
  };

  window.getDownloadStatus = () => {
    const { isDownloading } = store.chunkProcessingStatus;
    return isDownloading;
  };

  window.titleAlert = (message, duration) => {
    utils.titleAlert(message, duration);
  };

  window.continueDownload = () => {
    if (downloadingQueue) downloadingQueue.continueDownload();
  };

  window.redirectPage = (location) => {
    window.location = location;
  };

  window.redirectAfterUpload = (location, seconds) => {
    if (!location) return;
    if (redirectAfterUploadActive) return;

    const delay = Number.parseInt(seconds, 10);
    if (!Number.isFinite(delay) || delay <= 0) {
      window.redirectPage(location);
      return;
    }

    redirectAfterUploadActive = true;
    const message = utils.createElement("p", ["text-primaryMain"], {});
    const redirectNow = beginRedirectCountdown(location, seconds, message);

    new Modal({
      title: "uploadCompleted",
      body: message,
      closable: false,
      hasCloseOption: false,
      zIndex: 100,
      buttons: [
        {
          label: "redirectNow",
          close: false,
          classes: ["mr-3"],
          onClick: redirectNow,
        },
      ],
    });
  };

  window.showUploadSummary = ({ files, location, seconds } = {}) => {
    if (redirectAfterUploadActive) return;
    redirectAfterUploadActive = true;

    const countdown = utils.createElement("p", ["upload-summary-countdown"], {});
    const redirectNow = beginRedirectCountdown(location, seconds, countdown);
    const buttons = [];

    if (redirectNow) {
      buttons.push({
        label: "redirectNow",
        close: false,
        classes: ["mr-3"],
        onClick: redirectNow,
      });
    } else {
      buttons.push({
        label: "close",
        close: true,
        classes: ["mr-3"],
        onClick: () => {
          redirectAfterUploadActive = false;
          if (location) {
            window.redirectPage(location);
          }
        },
      });
    }

    new Modal({
      title: "uploadCompleted",
      body: uploadSummaryBody(files, redirectNow ? countdown : null),
      closable: false,
      hasCloseOption: false,
      zIndex: 100,
      minWidth: "560px",
      extraClasses: ["upload-summary-dialog"],
      buttons,
    });
  };

  const crushdropInitial = readCrushdropPathFromHostDocument();
  if (crushdropInitial != null) {
    applyCrushdropUploadPathOnHost(crushdropInitial);
  }
};
